import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subscription, catchError, forkJoin, of, tap, timer } from 'rxjs';
import { environment } from '../../environments/environment';

export type BrandChangeStatus = 'Pending' | 'Approved' | 'Rejected' | 'Withdrawn';

export interface BrandFieldDiff {
  field: string;
  before: string | null;
  after: string | null;
}

/** A seller's change to their own brand waiting for staff (SubscriptionManagement BrandChangeView). */
export interface BrandChange {
  changeId: string;
  brandId: string;
  brandName: string | null;
  businessId: string;
  businessName: string | null;
  changeSummary: string | null;
  diffs: BrandFieldDiff[];
  status: BrandChangeStatus;
  submittedAt: string;
  reviewedAt: string | null;
  rejectionReason: string | null;
  /** Other businesses selling the brand on the marketplace; null when unknown. */
  otherBusinessCount: number | null;
  /** The proposed name now clashes with another brand: approving is refused. */
  collision: string | null;
}

export interface BrandChangePage {
  items: BrandChange[];
  total: number;
  page: number;
  pageSize: number;
}

/** A seller brand waiting for staff: held for review, or the owner asked to be verified. */
export interface BrandAttentionItem {
  brandId: string;
  brandName: string;
  slug: string | null;
  ownerBusinessId: string | null;
  ownerBusinessName: string | null;
  isActive: boolean;
  isVerified: boolean;
  needsReview: boolean;
  verificationRequestedAt: string | null;
  createdAt: string;
  /** The platform brand its name contains, when that is why it was held. */
  collidesWith: string | null;
  websiteUrl?: string | null;
  /** The owner proved they run the website with a DNS record: evidence for Verify. */
  websiteProven?: boolean;
}

/**
 * Staff review of seller-created brands (product brand spec section 7.5), backed by
 * SubscriptionManagement api/admin/brand-changes (changes waiting for approval) and
 * api/admin/brands/review (brands held for review or asking to be verified).
 *
 * Also owns the sidebar badge: both queues added together, polled once a minute while an
 * admin is signed in. The review target is 3 business days.
 */
@Injectable({ providedIn: 'root' })
export class BrandReviewAdminService {
  static readonly PAGE_SIZE = 20;
  private static readonly POLL_MS = 60_000;

  private readonly changesUrl = environment.subscriptionApiUrl + '/api/admin/brand-changes';
  private readonly brandsUrl = environment.subscriptionApiUrl + '/api/admin/brands';

  /** Changes plus brands waiting, for the sidebar badge. */
  readonly waitingCount = signal(0);
  private polling?: Subscription;

  constructor(private http: HttpClient) {}

  getChanges(status: BrandChangeStatus | 'All', pageNumber: number): Observable<BrandChangePage> {
    const params = new HttpParams()
      .set('status', status)
      .set('pageNumber', pageNumber)
      .set('pageSize', BrandReviewAdminService.PAGE_SIZE);
    return this.http.get<BrandChangePage>(this.changesUrl, { params });
  }

  approve(changeId: string): Observable<BrandChange> {
    return this.http.post<BrandChange>(`${this.changesUrl}/${encodeURIComponent(changeId)}/approve`, {})
      .pipe(tap(() => this.refreshCount()));
  }

  reject(changeId: string, reason: string): Observable<BrandChange> {
    return this.http.post<BrandChange>(`${this.changesUrl}/${encodeURIComponent(changeId)}/reject`, { reason })
      .pipe(tap(() => this.refreshCount()));
  }

  getAttention(): Observable<BrandAttentionItem[]> {
    return this.http.get<BrandAttentionItem[]>(`${this.brandsUrl}/review`);
  }

  verify(brandId: string): Observable<void> {
    return this.http.post<void>(`${this.brandsUrl}/${encodeURIComponent(brandId)}/verify`, {})
      .pipe(tap(() => this.refreshCount()));
  }

  unverify(brandId: string): Observable<void> {
    return this.http.post<void>(`${this.brandsUrl}/${encodeURIComponent(brandId)}/unverify`, {})
      .pipe(tap(() => this.refreshCount()));
  }

  clearReview(brandId: string): Observable<void> {
    return this.http.post<void>(`${this.brandsUrl}/${encodeURIComponent(brandId)}/clear-review`, {})
      .pipe(tap(() => this.refreshCount()));
  }

  /** Starts the badge poll (idempotent). */
  startPolling(): void {
    if (this.polling) return;
    this.polling = timer(0, BrandReviewAdminService.POLL_MS).subscribe(() => this.refreshCount());
  }

  stopPolling(): void {
    this.polling?.unsubscribe();
    this.polling = undefined;
    this.waitingCount.set(0);
  }

  refreshCount(): void {
    // A badge is not worth an error on screen: a queue that can't answer counts as 0 and the
    // next poll tries again.
    const count = (url: string) => this.http.get<{ count: number }>(url).pipe(catchError(() => of({ count: 0 })));
    forkJoin([count(`${this.changesUrl}/count`), count(`${this.brandsUrl}/review/count`)])
      .subscribe(([changes, brands]) => this.waitingCount.set((changes?.count ?? 0) + (brands?.count ?? 0)));
  }

  /** Whole days something has waited, shown in the queues. */
  static daysWaiting(since: string | null | undefined, now = new Date()): number {
    if (!since) return 0;
    const ms = now.getTime() - new Date(since).getTime();
    return Math.max(0, Math.floor(ms / 86_400_000));
  }

  /** Weekdays elapsed since, for the 3-business-day review target (public holidays not counted). */
  static businessDaysWaiting(since: string | null | undefined, now = new Date()): number {
    if (!since) return 0;
    const day = new Date(since);
    day.setUTCHours(0, 0, 0, 0);
    let count = 0;
    // Bounded: anything this old is overdue whatever the exact count.
    for (let i = 0; i < 60 && day.getTime() < now.getTime(); i++) {
      day.setUTCDate(day.getUTCDate() + 1);
      const weekday = day.getUTCDay();
      if (day.getTime() <= now.getTime() && weekday !== 0 && weekday !== 6) count++;
    }
    return count;
  }
}
